/**
 * Customer notification drafts of a vulnerability case (DEV-100, DEV-134): one
 * Benachrichtigung per customer running an affected version of the product,
 * naming all of its instances with their version (DEV-128) — one draft per
 * Benachrichtigung.
 * Rendered inside the notification workspace (NotificationsPage); assessing
 * happens in the triage.
 *
 * `POST …/cases/{case}/email` is MUTATING despite its name: it
 * (re-)runs the Benachrichtigung fan-out (one card per Kunde) and only then
 * drafts one e-mail per Benachrichtigung from the saved Security Advisory. That is why it
 * runs when the panel opens rather than on render, and why it is guarded
 * against React's StrictMode double-mount:
 * a second POST would be harmless but would log a second fan-out run.
 *
 * Drafts come only from the released Security Advisory of the case (DEV-134); while it is
 * not released nothing can be selected or queued, not even a saved draft.
 *
 * "Senden" queues the selected drafts in the Postausgang (DEV-93): recipient,
 * subject, the mail as HTML (with the Markdown as its text alternative) and the
 * instance list are frozen there and sent from the Postausgang. This tab only
 * writes and queues.
 */

import type { MessageDescriptor } from '@lingui/core';
// Core `t` is aliased: the components below take their own `t` from useLingui().
import { msg, plural, t as translate } from '@lingui/core/macro';
import { Plural, Trans, useLingui } from '@lingui/react/macro';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import Banner from '../../components/Banner';
import Chip, { ChipList } from '../../components/Chip';
import Spinner, { PageSpinner } from '../../components/Spinner';
import { useToast } from '../../components/Toast';
import {
  ApiError,
  errorMessage,
  fetchEmailDrafts,
  queueNotifications,
  saveNotificationDraft,
} from '../../lib/api';
import { fmtValue } from '../../lib/format';
import { missingPermissionTitle } from '../../lib/permissions';
import type { AdvisoryStatus, EmailDraft, EmailDraftsResponse, EmailDraftsResult } from '../../lib/types';

import MarkdownEditor from '../../components/markdown/MarkdownEditor';
import { draftKey, NOTIFICATION_STATUS_LABELS, notificationTone } from './eventHelpers';
import MailPreview from './MailPreview';
import { markdownToHtml } from './mailText';

export interface NotificationDraftsProps {
  /** The vulnerability case (product × CVE) whose customers are notified (DEV-134). */
  caseId: number;
  /** Saved advisory revision — a new revision reloads the drafts. */
  advisoryRevision: number;
  /** `notification.send` — false means read-only: drafts visible, no queuing. */
  canSend: boolean;
  /** Called after drafts were queued so the parent can refetch rows and the Postausgang. */
  onQueued: () => void;
  /** Where the advisory is written: the case tab of the triage dialog (DEV-134). */
  advisoryLink: string;
  /** Unsaved edits in any draft (for the close guard). */
  onDirtyChange?: (dirty: boolean) => void;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; result: EmailDraftsResult }
  | { kind: 'error'; message: string; status: number | null };

type DraftText = { betreff: string; inhalt: string };

/** Kept out of the message text: ICU would read the braces as an argument. */
const PLACEHOLDER_SYNTAX = '{{…}}';

/** Why a draft cannot be queued right now, or null when it can. */
function blockedReason(draft: EmailDraft): string | null {
  if (draft.benachrichtigung.status !== 'OFFEN') return null;  // shown by its status chip
  if (draft.source === 'missing') return translate`Kein freigegebenes Security Advisory.`;
  if (!draft.kunde.email) return translate`Keine Kontakt-E-Mail hinterlegt.`;
  if (draft.stale) return translate`Instanzliste seit dem Speichern geändert — Entwurf neu speichern oder verwerfen.`;
  return null;
}

/** Without a released advisory no mail is queued (DEV-134); the way leads into the triage. */
function AdvisoryNotice({ status, link }: Readonly<{ status: AdvisoryStatus | null; link: string }>): ReactNode {
  const { t } = useLingui();
  if (status === 'RELEASED') return null;
  return (
    <Banner kind="warn" sub={<Link className="ghost small" to={link}>{status ? t`In der Triage freigeben` : t`In der Triage verfassen`}</Link>}>
      {status ? (
        <Trans>
          Security Advisory nicht freigegeben: Kundenmails entstehen erst aus dem freigegebenen Advisory. Es wird in
          der Triage einmal je Produkt im Schwachstellenfall verfasst und freigegeben.
        </Trans>
      ) : (
        <Trans>
          Kein Security Advisory gespeichert: Kundenmails entstehen erst aus dem freigegebenen Advisory. Es wird in
          der Triage einmal je Produkt im Schwachstellenfall verfasst und freigegeben.
        </Trans>
      )}
    </Banner>
  );
}

function isQueueable(draft: EmailDraft): boolean {
  return draft.benachrichtigung.status === 'OFFEN' && blockedReason(draft) === null;
}

const SOURCE_LABELS: Readonly<Record<EmailDraft['source'], MessageDescriptor>> = {
  advisory: msg`Aus dem Security Advisory`,
  saved: msg`Gespeicherter Entwurf`,
  missing: msg`Kein Entwurf — Security Advisory fehlt`,
  queued: msg`Eingefrorener Text im Postausgang`,
  sent: msg`Versendeter Text`,
};

/** Which Benachrichtigung a draft is, whether it follows up on an earlier one, and its state. */
function NotificationChips({ draft }: Readonly<{ draft: EmailDraft }>): ReactNode {
  const { t } = useLingui();
  const { id, previous_id: previousId, status } = draft.benachrichtigung;
  return (
    <ChipList>
      {id === null ? <Chip tone="warn"><Trans>Keine Benachrichtigung angelegt</Trans></Chip> : <Chip mono><Trans>Benachrichtigung #{id}</Trans></Chip>}
      {previousId === null ? null : (
        <Chip tone="info" title={t`Für Anlagen, die nach der vorherigen Benachrichtigung hinzugekommen sind`}>
          <Trans>Nachtrag zu #{previousId}</Trans>
        </Chip>
      )}
      <Chip tone={notificationTone(status)}>{NOTIFICATION_STATUS_LABELS[status] ?? status}</Chip>
    </ChipList>
  );
}

interface DraftCardProps {
  draft: EmailDraft;
  text: DraftText;
  dirty: boolean;
  /** Saved in this session (the server payload predates it). */
  savedHere: boolean;
  selected: boolean;
  canSend: boolean;
  busy: boolean;
  saving: boolean;
  onSelect: (selected: boolean) => void;
  onText: (text: DraftText) => void;
  onSave: () => void;
  onDiscard: () => void;
}

/** Recipient line with the selection box and the source of the text. */
function DraftHeader({ draft, dirty, savedHere, selected, canSend, busy, onSelect }: Readonly<DraftCardProps>): ReactNode {
  const { t, i18n } = useLingui();
  const reason = blockedReason(draft);
  const sourceLabel = i18n._(savedHere ? SOURCE_LABELS.saved : SOURCE_LABELS[draft.source]);
  const savedBy = draft.source === 'saved' && draft.saved_by && !savedHere ? ` · ${draft.saved_by}` : '';
  const showPlaceholders = draft.placeholders > 0 && draft.benachrichtigung.status === 'OFFEN';
  const customer = fmtValue(draft.kunde.name);
  const instanceCount = draft.instanzen.length;
  const placeholders = draft.placeholders;
  return (
    <>
      <div className="to">
        {draft.benachrichtigung.status === 'OFFEN' ? (
          <input
            type="checkbox"
            checked={selected}
            disabled={!canSend || !isQueueable(draft) || busy}
            aria-label={t`${customer} zum Senden auswählen`}
            title={canSend ? reason ?? undefined : missingPermissionTitle('notification.send')}
            onChange={(e) => onSelect(e.target.checked)}
          />
        ) : null}{' '}
        <strong>{customer}</strong> · <Trans>An:</Trans>{' '}
        {draft.kunde.email ?? <span className="muted"><Trans>(keine E-Mail hinterlegt)</Trans></span>}
        {' '}· <Plural value={instanceCount} one="# Anlage" other="# Anlagen" />
      </div>
      <ChipList>
        <Chip tone={draft.source === 'missing' ? 'warn' : 'info'}>{sourceLabel}{savedBy}</Chip>
        {dirty ? <Chip tone="warn"><Trans>Ungespeicherte Änderungen</Trans></Chip> : null}
        {showPlaceholders ? (
          <Chip tone="warn" title={t`Platzhalter ${PLACEHOLDER_SYNTAX} vor dem Versand ersetzen`}><Trans>{placeholders} offene Platzhalter</Trans></Chip>
        ) : null}
      </ChipList>
      {reason !== null && draft.source !== 'missing' ? <Banner kind="warn">{reason}</Banner> : null}
    </>
  );
}

/** Subject and Markdown text of an open draft, with save and discard. */
function DraftEditor({ draft, text, dirty, savedHere, busy, saving, onText, onSave, onDiscard }: Readonly<DraftCardProps>): ReactNode {
  const { t } = useLingui();
  const key = draftKey(draft);
  const customer = fmtValue(draft.kunde.name);
  return (
    <>
      <label className="visually-hidden" htmlFor={`mail-subject-${key}`}><Trans>Betreff</Trans></label>
      <input
        type="text"
        id={`mail-subject-${key}`}
        className="mail-subject-input"
        value={text.betreff}
        disabled={busy}
        onChange={(e) => onText({ ...text, betreff: e.target.value })}
      />
      <span className="visually-hidden" id={`mail-body-label-${key}`}><Trans>Text an {customer}</Trans></span>
      <div className="mail-body-input">
        <MarkdownEditor
          id={`mail-body-${key}`}
          labelledBy={`mail-body-label-${key}`}
          value={text.inhalt}
          disabled={busy}
          rows={9}
          onChange={(inhalt) => onText({ ...text, inhalt })}
        />
      </div>
      <div className="mail-draft-actions">
        {draft.source === 'saved' || savedHere ? (
          <button type="button" className="ghost small" disabled={busy}
            title={t`Gespeicherten Entwurf verwerfen; danach gilt wieder das Advisory`}
            onClick={onDiscard}>
            <Trans>Verwerfen</Trans>
          </button>
        ) : null}
        <button type="button" className="ghost small" disabled={busy || !(dirty || draft.stale)}
          title={t`Bearbeiteten Text speichern, um ihn später weiter zu bearbeiten oder zu senden`}
          onClick={onSave}>
          {saving ? <Spinner /> : null} <Trans>Entwurf speichern</Trans>
        </button>
      </div>
    </>
  );
}

/** One customer's mail: selectable and editable while open, read-only once queued or sent. */
function DraftCard(props: Readonly<DraftCardProps>): ReactNode {
  const { t } = useLingui();
  const { draft, text, canSend } = props;
  const customer = fmtValue(draft.kunde.name);
  const editable = canSend && draft.benachrichtigung.status === 'OFFEN' && draft.source !== 'missing';
  let body: ReactNode = null;
  if (editable) {
    body = <DraftEditor {...props} />;
  } else if (draft.html) {
    body = <><div className="subj">{text.betreff}</div><MailPreview html={draft.html} title={t`Mail an ${customer}`} /></>;
  } else if (draft.source !== 'missing') {
    body = <><div className="subj">{text.betreff}</div><pre>{text.inhalt}</pre></>;
  }
  return (
    <article className="mail">
      <DraftHeader {...props} />
      {body}
      <div className="triage-mail-foot">
        <NotificationChips draft={draft} />
      </div>
    </article>
  );
}

export function NotificationDrafts({
  caseId,
  advisoryRevision,
  canSend,
  onQueued,
  advisoryLink,
  onDirtyChange,
}: Readonly<NotificationDraftsProps>): ReactNode {
  const { t } = useLingui();
  const toast = useToast();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [actionError, setActionError] = useState<string | null>(null);
  const [queueErrors, setQueueErrors] = useState<readonly string[]>([]);
  /** Edited subject/text per draft; absent = the server draft unchanged. */
  const [edits, setEdits] = useState<Readonly<Record<number, DraftText>>>({});
  /** Last saved text per draft in this session (the baseline of `edits`). */
  const [savedTexts, setSavedTexts] = useState<Readonly<Record<number, DraftText>>>({});
  const [selected, setSelected] = useState<ReadonlySet<number>>(new Set());
  /** Bumped to reload the drafts, e.g. after a saved draft was discarded. */
  const [reload, setReload] = useState(0);
  const [savingDraft, setSavingDraft] = useState<number | null>(null);
  const [queuing, setQueuing] = useState(false);
  /**
   * The in-flight (or completed) fan-out request, keyed by event.
   *
   * StrictMode mounts effects twice in development, and this POST re-runs the
   * Benachrichtigung fan-out — so it must fire exactly once. Guarding with a
   * plain "already started" flag is NOT enough: the first cleanup would cancel
   * the only subscription while the second pass skips the request altogether,
   * leaving the dialog on its spinner forever. Caching the promise instead lets
   * the second pass re-subscribe to the same request.
   */
  const request = useRef<{ key: string; promise: Promise<EmailDraftsResponse> } | null>(null);

  useEffect(() => {
    const key = `${caseId}:${advisoryRevision}:${reload}`;
    let cancelled = false;
    const cached = request.current;
    const promise =
      cached !== null && cached.key === key ? cached.promise : fetchEmailDrafts(caseId);
    request.current = { key, promise };
    promise.then(
      (response) => {
        if (!cancelled) setState({ kind: 'ready', result: response });
      },
      (err: unknown) => {
        if (cancelled) return;
        setState({
          kind: 'error',
          message: errorMessage(err),
          status: err instanceof ApiError ? err.status : null,
        });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [caseId, advisoryRevision, reload]);

  const baseOf = useCallback(
    (draft: EmailDraft): DraftText => savedTexts[draftKey(draft)] ?? { betreff: draft.betreff, inhalt: draft.inhalt },
    [savedTexts],
  );
  const textOf = useCallback(
    (draft: EmailDraft): DraftText => edits[draftKey(draft)] ?? baseOf(draft),
    [edits, baseOf],
  );
  const isDirty = useCallback((draft: EmailDraft): boolean => {
    const edited = edits[draftKey(draft)];
    const base = baseOf(draft);
    return edited !== undefined && (edited.betreff !== base.betreff || edited.inhalt !== base.inhalt);
  }, [edits, baseOf]);

  const drafts = state.kind === 'ready' ? state.result.drafts : [];
  const released = state.kind === 'ready' && state.result.advisory_status === 'RELEASED';
  const anyDirty = drafts.some(isDirty);
  useEffect(() => onDirtyChange?.(anyDirty), [anyDirty, onDirtyChange]);

  const forget = (key: number): void => {
    setSavedTexts((current) => { const next = { ...current }; delete next[key]; return next; });
    setEdits((current) => { const next = { ...current }; delete next[key]; return next; });
  };

  const saveDraft = async (draft: EmailDraft, discard = false): Promise<void> => {
    const id = draft.benachrichtigung.id;
    if (id === null) return;
    const text = textOf(draft);
    setSavingDraft(draftKey(draft));
    setActionError(null);
    try {
      // Saved as Markdown: it stays editable; plain text is made when queuing.
      await saveNotificationDraft({ benachrichtigung_ids: [id], betreff: text.betreff, inhalt: text.inhalt, discard });
      if (discard) {
        forget(draftKey(draft));
        toast.success(t`Gespeicherter Entwurf verworfen — der Text kommt wieder aus dem Advisory.`);
      } else {
        setSavedTexts((current) => ({ ...current, [draftKey(draft)]: text }));
        toast.success(t`Entwurf gespeichert.`);
      }
      // The server decides again whether the draft is current (DEV-93).
      setReload((r) => r + 1);
    } catch (err) {
      setActionError(errorMessage(err));
    } finally {
      setSavingDraft(null);
    }
  };

  const queueable = released ? drafts.filter(isQueueable) : [];
  const chosen = queueable.filter((draft) => selected.has(draftKey(draft)));

  const queueSelected = async (): Promise<void> => {
    if (chosen.length === 0) return;
    const question = t`${plural(chosen.length, { one: '# Benachrichtigung', other: '# Benachrichtigungen' })} in den Postausgang stellen? Empfänger, Text und Anlagenliste werden dabei eingefroren.`;
    if (!window.confirm(question)) return;
    setQueuing(true);
    setActionError(null);
    try {
      const result = await queueNotifications({
        case_id: caseId,
        // The customer receives HTML; the Markdown is frozen as its text alternative.
        items: chosen.map((draft) => {
          const text = textOf(draft);
          return {
            id: draft.benachrichtigung.id as number,
            betreff: text.betreff,
            inhalt: text.inhalt,
            html: markdownToHtml(text.inhalt),
          };
        }),
      });
      setQueueErrors(result.errors.map((error) => {
        const draft = drafts.find((d) => d.benachrichtigung.id === error.id);
        return `${fmtValue(draft?.kunde.name)}: ${error.detail}`;
      }));
      for (const id of result.queued) forget(id);
      setSelected(new Set());
      if (result.queued.length > 0) {
        toast.success(t`${plural(result.queued.length, { one: '# Benachrichtigung', other: '# Benachrichtigungen' })} im Postausgang.`);
        onQueued();
      }
      setReload((r) => r + 1);
    } catch (err) {
      setActionError(errorMessage(err));
    } finally {
      setQueuing(false);
    }
  };

  const busy = queuing || savingDraft !== null;
  const selectedCount = chosen.length;
  // A customer may have a sent notification and a follow-up side by side.
  const customerCount = new Set(drafts.map((draft) => draft.kunde.id)).size;

  if (state.kind === 'loading') return <PageSpinner text={t`Benachrichtigungen werden erzeugt…`} />;
  if (state.kind === 'error') {
    const hint = {
      409: t`Bestätigen Sie die Produktversion zuerst in der Triage als „Betroffen“.`,
      404: t`Die Produktversion existiert nicht mehr, laden Sie die Liste neu.`,
    }[state.status ?? 0] ?? t`Es wurde keine Benachrichtigung erzeugt.`;
    return <div className="notification-drafts"><Banner kind="error" sub={hint}>{state.message}</Banner></div>;
  }

  return (
    <div className="notification-drafts">
      {actionError === null ? null : <Banner kind="error">{actionError}</Banner>}
      {queueErrors.length === 0 ? null : (
        <Banner kind="warn" sub={<ul>{queueErrors.map((line) => <li key={line}>{line}</li>)}</ul>}>
          <Trans>Nicht alle ausgewählten Benachrichtigungen wurden eingereiht.</Trans>
        </Banner>
      )}

      {drafts.length === 0 ? (
        <Banner kind="warn">
          <Trans>
            Keine betroffenen Kunden: Nach bekannter Konfiguration läuft keine Produktinstanz mit
            dieser Komponentenversion.
          </Trans>
        </Banner>
      ) : (
        <Banner kind="info">
          {t`${plural(customerCount, { one: '# Kunde', other: '# Kunden' })} betroffen, eine Mail pro Kunde mit allen betroffenen Anlagen. Passen Sie den Text bei Bedarf an, wählen Sie die Kunden aus und stellen Sie die Mails in den Postausgang — versendet und bestätigt wird dort.`}
        </Banner>
      )}

      <AdvisoryNotice status={state.result.advisory_status} link={advisoryLink} />

      {queueable.length === 0 ? null : (
        <div className="mail-queue-bar">
          <label className="check">
            <input
              type="checkbox"
              checked={chosen.length === queueable.length}
              disabled={!canSend || busy}
              onChange={(e) => setSelected(new Set(e.target.checked ? queueable.map(draftKey) : []))}
            />
            <span><Trans>Alle auswählen</Trans></span>
          </label>
          <div className="spacer" />
          <button
            type="button"
            className="primary small"
            disabled={!canSend || busy || chosen.length === 0}
            title={canSend ? undefined : missingPermissionTitle('notification.send')}
            onClick={() => void queueSelected()}
          >
            {queuing ? <Spinner variant="on-accent" /> : null} {t`Ausgewählte senden (${selectedCount})`}
          </button>
        </div>
      )}

      {drafts.map((draft) => {
        const key = draftKey(draft);
        return (
          <DraftCard
            key={key}
            draft={draft}
            text={textOf(draft)}
            dirty={isDirty(draft)}
            savedHere={savedTexts[key] !== undefined}
            selected={selected.has(key)}
            canSend={canSend && released}
            busy={busy}
            saving={savingDraft === key}
            onSelect={(on) => setSelected((current) => {
              const next = new Set(current);
              if (on) next.add(key);
              else next.delete(key);
              return next;
            })}
            onText={(next) => setEdits((current) => ({ ...current, [key]: next }))}
            onSave={() => void saveDraft(draft)}
            onDiscard={() => void saveDraft(draft, true)}
          />
        );
      })}
    </div>
  );
}

export default NotificationDrafts;
